import {
  AgendaSpeaker,
  PublicEventDetailsResponse,
  PublicExhibitionItem,
  PublicSessionItem,
  Portfolio,
  ResponsePaginationType,
} from '@hubs101/booking-api-skd-client/lib/types';
import { Document, Link, Page, Text, View } from '@react-pdf/renderer';
import { useCallback, useMemo } from 'react';

import weLogoBlack from '@assets/images/we_conect_logo_black.png';
import weLogoWhite from '@assets/images/we_conect_logo_white.png';
import { adminEventsRouteFormat } from '@constants/paths';
import { useTranslation } from 'react-i18next';
import { DefaultTheme } from 'styled-components';
import { APP_HOST } from '@utils/apiHooks';
import BusinessPartner from './components/BusinessPartner';

import Exhibitions from './components/Exhibitions';
import KeyTopics from './components/KeyTopics';
import Moderators from './components/Moderators';
import MultiTouchPoint from './components/MultiTouchPoint';
import TargetGroups from './components/TargetGroups';
import { styles } from './styles';
import { getImageSrc } from '@utils/fn';

import TitlePage from './pages/TitlePage';
import AgendaBodyPages from './pages/AgendaBodyPages';
import ContactPage from './pages/ContactPage';
import TestimonialsBookNow from './components/TestimonialsBookNow';
import { Ticket } from '@composites/Tickets';
import AlreadyRegistered from './components/AlreadyRegistered';
import IncreaseYourSales from './pages/IncreaseYourSales';
import ColocatedEvents from './pages/ColocatedEvents';
import MatchMakingOne2OneMeetingsPage from './pages/MatchMakingOne2OneMeetingsPage';
import EventLocation from './components/EventLocation';
import BookNowPage from './pages/BookNowPage';
import IntermediatePage from './pages/IntermediatePage';
import { SafeImage } from './components/SafeImage';

interface AgendaPdfContentProps {
  event: PublicEventDetailsResponse;
  coLocatedEvents: PublicEventDetailsResponse[] | undefined;
  portfolios: ResponsePaginationType<Portfolio> | undefined;
  theme: DefaultTheme;
  groupedAgendasSessions: PublicSessionItem[][];
  agendaPdfDates: string[];
  agendaSpeakers: AgendaSpeaker[];
  exhibitions: PublicExhibitionItem[];
  tickets: Ticket[];
  advertisements: any[] | undefined;
}

const AgendaPdfContent = ({
  event,
  coLocatedEvents,
  portfolios,
  groupedAgendasSessions,
  agendaPdfDates,
  agendaSpeakers,
  exhibitions,
  tickets,
  advertisements,
}: AgendaPdfContentProps) => {
  const { t } = useTranslation();

  const Header = useCallback(
    ({
      event,
      lightMode,
    }: {
      event: PublicEventDetailsResponse;
      lightMode: boolean;
    }) => {
      const eventHomePageUrl = `${APP_HOST}/events/${event?.slug}`;
      const bookNowUrl = `${APP_HOST}/events/${event?.slug}${t('book-now-url')}`;
      const partnerUrl = `${APP_HOST}/events/${event?.slug}${t('partnering-packages-url')}`;

      return (
        <View style={styles.headerWrapper} fixed>
          <Link src={eventHomePageUrl} style={{ textDecoration: 'none' }}>
            <SafeImage
              src={getImageSrc(
                lightMode ? event?.images?.logoTransparent : event?.images?.logo
              )}
              style={styles.eventLogo}
              debugLabel="event?.images?.logoTransparent"
            />
          </Link>

          <View style={styles.buttonsContainer}>
            <Link
              src={bookNowUrl}
              style={{
                ...styles.primaryButton,
                backgroundColor: event?.design?.themeColor || 'black',
              }}
            >
              <Text style={styles.primaryButtonText}>{t('book-now')}</Text>
            </Link>

            <Link
              src={partnerUrl}
              style={{
                ...styles.secondaryButton,
                borderColor: lightMode ? 'white' : 'black',
              }}
            >
              <Text
                style={{
                  ...styles.secondaryButtonText,
                  color: lightMode ? 'white' : 'black',
                }}
              >
                {t('apply-as-partner-pdf')}
              </Text>
            </Link>
          </View>
        </View>
      );
    },
    []
  );

  const Footer = useCallback(
    ({ lightMode }: { lightMode?: boolean }) => {
      const eventLink = `${APP_HOST}${adminEventsRouteFormat(event?.slug, '').slice(0, -1)}
            `;
      return (
        <View
          style={{
            ...styles.footer,
            backgroundColor: lightMode ? 'transparent' : '#EEEEEE',
          }}
          fixed
        >
          <View style={styles.footerLeftItem}>
            <Link
              src={eventLink}
              style={{
                ...styles.eventName,
                color: lightMode
                  ? 'white'
                  : (event?.design?.themeColor ?? 'black'),
                textDecoration: 'none',
              }}
            >
              {t('visit-the-website-here')}
            </Link>
          </View>

          <View style={styles.footerMiddleItem}>
            <Text
              style={{
                ...styles.pageNr,
                color: lightMode ? 'white' : '#27343d',
              }}
              render={({ pageNumber, totalPages }) => {
                if (pageNumber > 1) {
                  return `${t('page')} ${pageNumber}`;
                }
                return '';
              }}
              fixed
            />
            <Text
              style={{
                ...styles.heartIcon,
                color: lightMode ? 'white' : '#27343d',
              }}
            >
              {t('agenda-pdf-footer-text-1')} {'\uf004'}{' '}
              {t('agenda-pdf-footer-text-2')}
            </Text>
          </View>

          <View style={styles.footerRightItem}>
            <SafeImage
              style={styles.logo}
              src={lightMode ? weLogoWhite : weLogoBlack}
              debugLabel="footerRightItem-weLogo"
            />
          </View>
        </View>
      );
    },
    [event?.design?.themeColor, event?.slug, t]
  );

  const homeTickets = useMemo<Ticket[]>(() => {
    const wc: any = event?.website_content || {};
    const wcTickets = wc?.tickets || [];

    const homeTicketIds = Array.isArray(wcTickets)
      ? wcTickets
          .filter((t: any) => t.page === 'home')
          .map((t: any) => t.ticket)
      : [];

    const filtered = tickets.filter(t => homeTicketIds.includes(t.id));

    const further: Ticket = {
      ...filtered[0],
      id: 'further-event-passes',
      name: t('further-event-passes'),
      price: 0,
    };

    return [...filtered, further];
  }, [event?.website_content, tickets, t]);

  /** ---------- Debug Wrapper ---------- */
  const errorHandlerWrapper = (
    label: string,
    renderFn: () => JSX.Element | null
  ) => {
    try {
      console.log(`Rendering ${label}`);
      return renderFn();
    } catch (err) {
      console.error(`Error rendering ${label}:`, err);
      return null;
    }
  };

  return (
    <Document>
      {errorHandlerWrapper('Title Page', () => (
        <TitlePage
          event={event}
          coLocatedEvents={coLocatedEvents}
          portfolios={portfolios}
          footer={<Footer />}
        />
      ))}

      {errorHandlerWrapper('Key Topics', () => (
        <Page size="A4" style={{ paddingBottom: 100 }}>
          <Header event={event} lightMode={false} />
          <KeyTopics event={event} />
          <Footer />
        </Page>
      ))}

      {errorHandlerWrapper('MultiTouchPoint', () => (
        <Page size="A4" style={{ paddingBottom: 60 }}>
          <Header event={event} lightMode={false} />
          <MultiTouchPoint event={event} />
          <Footer />
        </Page>
      ))}

      {errorHandlerWrapper('TargetGroups', () => (
        <Page size="A4" style={{ paddingBottom: 100 }}>
          <Header event={event} lightMode={false} />
          <TargetGroups event={event} />
          <Footer />
        </Page>
      ))}

      {errorHandlerWrapper('AlreadyRegistered', () => (
        <Page
          size="A4"
          style={{ backgroundColor: '#0F91AE', paddingBottom: 100 }}
        >
          <Header event={event} lightMode />
          <AlreadyRegistered event={event} advertisements={advertisements} />
          <Footer lightMode />
        </Page>
      ))}

      {errorHandlerWrapper('TestimonialsBookNow', () => (
        <Page size="A4" style={{ paddingBottom: 60 }}>
          <Header event={event} lightMode={false} />
          <TestimonialsBookNow event={event} tickets={homeTickets} />
          <Footer />
        </Page>
      ))}

      {errorHandlerWrapper('Moderators', () => (
        <Page size="A4" style={{ paddingBottom: 100 }}>
          <Header event={event} lightMode={false} />
          <Moderators event={event} agendaSpeakers={agendaSpeakers} />
          <Footer />
        </Page>
      ))}

      {errorHandlerWrapper('AgendaBodyPages', () => (
        <AgendaBodyPages
          event={event}
          coLocatedEvents={coLocatedEvents}
          portfolios={portfolios}
          agendaPdfDates={agendaPdfDates}
          groupedAgendasSessions={groupedAgendasSessions}
          header={<Header event={event} lightMode={false} />}
          footer={<Footer />}
        />
      ))}

      {errorHandlerWrapper('IntermediatePage', () => (
        <IntermediatePage
          event={event}
          coLocatedEvents={coLocatedEvents}
          portfolios={portfolios}
          header={<Header event={event} lightMode />}
          footer={<Footer lightMode />}
        />
      ))}

      {errorHandlerWrapper('Exhibitions', () => (
        <Page size="A4" style={{ paddingBottom: 60 }}>
          <Header event={event} lightMode={false} />
          <Exhibitions event={event} />
          <Footer />
        </Page>
      ))}

      {errorHandlerWrapper('BusinessPartner', () => (
        <Page size="A4" style={{ paddingBottom: 100 }}>
          <Header event={event} lightMode={false} />
          <BusinessPartner event={event} exhibitions={exhibitions} />
          <Footer />
        </Page>
      ))}

      {errorHandlerWrapper('IncreaseYourSales', () => (
        <IncreaseYourSales
          event={event}
          coLocatedEvents={coLocatedEvents}
          portfolios={portfolios}
          header={<Header event={event} lightMode />}
          footer={<Footer />}
        />
      ))}

      {errorHandlerWrapper('ColocatedEvents', () =>
        event?.colocated ? (
          <ColocatedEvents
            event={event}
            coLocatedEvents={coLocatedEvents}
            portfolios={portfolios}
            header={<Header event={event} lightMode />}
            footer={<Footer />}
          />
        ) : null
      )}

      {errorHandlerWrapper('MatchMakingOne2OneMeetings', () => (
        <MatchMakingOne2OneMeetingsPage
          event={event}
          coLocatedEvents={coLocatedEvents}
          portfolios={portfolios}
          header={<Header event={event} lightMode={false} />}
          footer={<Footer />}
        />
      ))}

      {errorHandlerWrapper('EventLocation', () => (
        <Page size="A4" style={{ paddingBottom: 100 }}>
          <Header event={event} lightMode={false} />
          <EventLocation event={event} />
          <Footer />
        </Page>
      ))}

      {errorHandlerWrapper('BookNow', () => (
        <BookNowPage
          event={event}
          coLocatedEvents={coLocatedEvents}
          portfolios={portfolios}
          header={<Header event={event} lightMode={false} />}
          footer={<Footer />}
        />
      ))}

      {errorHandlerWrapper('Contact', () => (
        <ContactPage
          event={event}
          coLocatedEvents={coLocatedEvents}
          portfolios={portfolios}
          header={<Header event={event} lightMode />}
          footer={<Footer />}
        />
      ))}
    </Document>
  );
};

export default AgendaPdfContent;
